import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
    Button,
    Box,
    Modal,
    Typography,
    IconButton,
    TextField,
    CircularProgress,
    Stack,
    Switch
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SpeedIcon from "@mui/icons-material/Speed";
import PlayBack from "./PlayBack";
import Viajes from "./Viajes";
import StopIcon from '@mui/icons-material/LocalParking';
import paradaIcon from "../../../assets/images/parada.png";
import axios from "axios";
import { FaRoute } from "react-icons/fa";
import {
    GoogleMap,
    Polyline,
    Marker,
    OverlayView,
    useJsApiLoader
} from "@react-google-maps/api";
import mapStyles from "../../../assets/estilos/mapStyles";

const mapContainerStyle = {
    width: "100%",
    height: "100%"
};
const defaultCenter = { lat: -33.4489, lng: -70.6693 };
const colores = ["#2b90fd", "#f54291", "#00c853", "#ff6f00", "#6a1b9a"];
const botonCronos = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    backgroundColor: "#2FA4B8",
    "&:hover": {
        backgroundColor: "#258a9c"
    }
};

const ModalHistorial = ({ vehiculoId }) => {
    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [trips, setTrips] = useState([]);
    const [visibleTrips, setVisibleTrips] = useState(new Set());
    const [error, setError] = useState("");
    const [hoveredInfo, setHoveredInfo] = useState(null);
    const [playPoints, setPlayPoints] = useState([]);
    const [playIndex, setPlayIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const mapRef = useRef(null);
    const playbackTimer = useRef(null);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [vehicleIcon, setVehicleIcon] = useState(null);
    const [followVehicle, setFollowVehicle] = useState(true);
    const [markerIcon, setMarkerIcon] = useState(null);
    const [summary, setSummary] = useState(null);
    const [stopIcon, setStopIcon] = useState(null); // Lo conservamos por compatibilidad
    const [stopMaxMinutes, setStopMaxMinutes] = useState(30);
    const [openStopModal, setOpenStopModal] = useState(false);
    const [tempStopMinutes, setTempStopMinutes] = useState(30);

    // 🔥 Estado para controlar el zoom actual y agrupar paradas
    const [zoomActual, setZoomActual] = useState(12);

    const [speedRanges, setSpeedRanges] = useState([
        { min: 0, max: 50, color: "#00C853" },
        { min: 50, max: 100, color: "#FFD600" },
        { min: 100, max: 120, color: "#FF6F00" },
        { min: 120, max: Infinity, color: "#D50000" }
    ]);

    const getTripMarkerIcon = (index, type = "start", color = "#2FA4B8") => {
        const maps = window.google?.maps;
        if (!maps) return undefined;

        // Dejamos el número sin ceros a la izquierda para un look más limpio (ej: "1" en vez de "01")
        const number = String(index + 1);

        // Colores modernos: tu color institucional para inicio, y un rojo brillante (#ef4444) para fin
        const bg = type === "start" ? "#2FA4B8" : type === "end" ? "#ef4444" : color;

        // Diseño minimalista de Pin con sombra paralela y alto contraste
        const svg = `
        <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <filter id="pinShadow" x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="rgba(0,0,0,0.25)" />
                </filter>
            </defs>
            
            <!-- Cuerpo del Pin (Teardrop) -->
            <path d="M16 2C8.268 2 2 8.268 2 16c0 8.428 11.723 19.458 13.28 20.89a2 2 0 0 0 2.71 0C19.277 35.458 30 24.428 30 16 30 8.268 23.732 2 16 2z" 
                  fill="${bg}" 
                  filter="url(#pinShadow)"/>
            
            <!-- Círculo interior blanco para aportar limpieza visual -->
            <circle cx="16" cy="16" r="10" fill="#ffffff" />
            
            <!-- Número del viaje, usando el color del fondo para armonizar -->
            <text x="16" y="20.5" 
                font-size="12" 
                font-family="Inter, Arial, sans-serif" 
                font-weight="900" 
                text-anchor="middle" 
                fill="${bg}">
                ${number}
            </text>
        </svg>
        `;

        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new maps.Size(32, 40),
            // 🔥 CLAVE: Desplazamos el ancla al (16, 40) para que la punta inferior del pin toque la coordenada exacta
            anchor: new maps.Point(16, 40) 
        };
    };

    // 🔥 Icono moderno de cluster para paradas (Suma minutos) MEJORADO VISUALMENTE
    const createStopClusterIcon = useCallback((totalMinutes) => {
        const google = window.google;
        if (!google) return null;

        // Reduje un poco el tamaño general para que no pelee con el pin
        const size = totalMinutes < 60 ? 40 : totalMinutes < 180 ? 48 : 56;
        const displayTime = totalMinutes > 999 ? "+99" : totalMinutes;

        const svg = `
            <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
                <defs>
                    <linearGradient id="stopGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stop-color="#ef4444" />
                        <stop offset="100%" stop-color="#991b1b" />
                    </linearGradient>
                    <filter id="stopShadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feDropShadow dx="0" dy="4" stdDeviation="4" flood-color="rgba(0,0,0,0.5)" />
                    </filter>
                </defs>
                <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="url(#stopGrad)" opacity="0.25" />
                <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 8}" fill="url(#stopGrad)" filter="url(#stopShadow)" />
                <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 9}" fill="none" stroke="rgba(255,255,255,0.9)" stroke-width="2" />
                <text x="50%" y="45%" dominant-baseline="middle" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="${size * 0.26}" font-weight="900" fill="#ffffff" letter-spacing="-0.5">${displayTime}</text>
                <text x="50%" y="67%" dominant-baseline="middle" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="${size * 0.16}" font-weight="bold" fill="#fca5a5" text-transform="uppercase">min</text>
            </svg>
        `;

        return {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
            scaledSize: new google.maps.Size(size, size),
            // 🔥 CLAVE: En lugar de (size/2, size/2), usamos (size/2, -5). 
            // Esto empuja el círculo hacia abajo, separándolo de la punta del pin.
            anchor: new google.maps.Point(size / 2, -5)
        };
    }, []);

    const [openLegend, setOpenLegend] = useState(false);

    const segmentRef = useRef(0);
    const tRef = useRef(0);
    const currentPoint = playPoints[playIndex] || null;

    const [showSpeedColors, setShowSpeedColors] = useState(false);

    const speedRef = useRef(1);

    useEffect(() => {
        speedRef.current = speed;
    }, [speed]);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
    });
    
    const handleOpen = () => {
        setOpen(true);
        setSelectedDate(new Date()); // Reinicia la fecha a "hoy" automáticamente
    };
    // 🔥 Carga la ruta automáticamente al abrir el modal
    useEffect(() => {
        if (open && vehiculoId) {
            fetchRoute();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]); // Solo se ejecuta cuando el modal se abre o cierra
    
    const handleClose = () => {
        setOpen(false);
        setTrips([]);
        setVisibleTrips(new Set());
        setPlayPoints([]);
        setPlayIndex(0);
        setIsPlaying(false);
        setError("");
        if (playbackTimer.current) {
            clearInterval(playbackTimer.current);
            playbackTimer.current = null;
        }
    };
    
    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);
    
    function rumboTexto(deg) {
        if (deg === undefined || deg === null) return "-";
        const dirs = [
            "Norte",
            "Noreste",
            "Este",
            "Sureste",
            "Sur",
            "Suroeste",
            "Oeste",
            "Noroeste"
        ];
        return dirs[Math.round(deg / 45) % 8];
    }
    
    const fetchRouteWithStops = async (stopMinutes) => {
        setLoading(true);
        setError("");
        setTrips([]);

        try {
            const token = localStorage.getItem("token");
            const isoDate = selectedDate.toLocaleDateString("sv-SE");

            const res = await axios.post(
                process.env.REACT_APP_API_MAPA_HISTORIAL_VEHICULO,
                {
                    vehiculo_id: vehiculoId,
                    date: isoDate,
                    stop_max_minutes: stopMinutes
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = res.data;

            if (data.foto_url) setVehicleIcon(data.foto_url);
            if (!data.trips) return;

            setTrips(data.trips);
            setSummary(data.summary);
            setVisibleTrips(new Set(data.trips.map((_, i) => i)));

        } catch (err) {
            console.error(err);
            setError("No se pudo cargar la ruta");
        } finally {
            setLoading(false);
        }
    };
    
    const getRangeLabel = (range) => {
        if (range.max === Infinity) {
            return `${range.min}+ km/h`;
        }
        return `${range.min} - ${range.max} km/h`;
    };
    
    const fetchRoute = useCallback(async () => {
        setLoading(true);
        setError("");
        setTrips([]);
        try {
            const token = localStorage.getItem("token");
            const isoDate = selectedDate.toLocaleDateString("sv-SE");
            const res = await axios.post(
                process.env.REACT_APP_API_MAPA_HISTORIAL_VEHICULO,
                {
                    vehiculo_id: vehiculoId,
                    date: isoDate,
                    stop_max_minutes: stopMaxMinutes
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            const data = res.data;
            if (data.foto_url) {
                setVehicleIcon(data.foto_url);
            }
            if (!data.trips || data.trips.length === 0) return;

            setTrips(data.trips);
            setSummary(data.summary);
            setVisibleTrips(new Set(data.trips.map((_, i) => i)));
        } catch (err) {
            console.error(err);
            setError("No se pudo cargar la ruta");
        } finally {
            setLoading(false);
        }
    }, [vehiculoId, selectedDate, stopMaxMinutes]);
    
    const toggleTrip = (index) => {
        const newSet = new Set(visibleTrips);
        if (newSet.has(index)) newSet.delete(index);
        else newSet.add(index);
        setVisibleTrips(newSet);
    };
    
    const startPlayback = () => {
        if (!playPoints.length || playPoints.length < 2) return;

        if (playbackTimer.current) {
            clearInterval(playbackTimer.current);
            playbackTimer.current = null;
        }

        if (segmentRef.current >= playPoints.length - 1) {
            segmentRef.current = playPoints.length - 2;
            tRef.current = 0;
        }

        if (segmentRef.current < 0) {
            segmentRef.current = 0;
            tRef.current = 0;
        }

        setIsPlaying(true);

        playbackTimer.current = setInterval(() => {
            let segment = segmentRef.current;

            if (segment >= playPoints.length - 1) {
                pausePlayback();
                return;
            }

            const p1 = playPoints[segment];
            const p2 = playPoints[segment + 1];

            if (!p1 || !p2) {
                pausePlayback();
                return;
            }

            const dt = new Date(p2.timestamp) - new Date(p1.timestamp) || 1000;

            tRef.current += (16 * speedRef.current) / dt;

            if (tRef.current >= 1) {
                segmentRef.current++;
                tRef.current = 0;

                const next = playPoints[segmentRef.current];

                if (!next || segmentRef.current >= playPoints.length - 1) {
                    pausePlayback();
                    return;
                }

                const pos = { lat: next.lat, lng: next.lng };

                setMarkerPosition(pos);
                setPlayIndex(segmentRef.current);

                if (followVehicle && mapRef.current) {
                    mapRef.current.panTo(pos);

                    if (mapRef.current.getZoom() < 16) {
                        mapRef.current.setZoom(16);
                    }
                }

                return;
            }

            const lat = p1.lat + (p2.lat - p1.lat) * tRef.current;
            const lng = p1.lng + (p2.lng - p1.lng) * tRef.current;

            const newPos = { lat, lng };

            setMarkerPosition(newPos);

            if (followVehicle && mapRef.current) {
                mapRef.current.panTo(newPos);

                if (mapRef.current.getZoom() < 16) {
                    mapRef.current.setZoom(16);
                }
            }

        }, 30);
    };

    const isNearStop = (lat, lng, grupos, radiusMeters = 40) => {
        for (const grupo of grupos) {
            if (!grupo.stops) continue;

            for (const stop of grupo.stops) {
                const d = Math.hypot(stop.lat - lat, stop.lng - lng);

                if (d * 111000 < radiusMeters) {
                    return true;
                }
            }
        }
        return false;
    };
    
    const pausePlayback = () => {
        if (playbackTimer.current) {
            clearInterval(playbackTimer.current);
            playbackTimer.current = null;
        }
        setIsPlaying(false);
    };

    const changeSpeed = (value) => {
        setSpeed(value);
    };

    const getSpeedColor = (speed) => {
        const range = speedRanges.find(r => speed >= r.min && speed < r.max);
        return range ? range.color : "#999";
    };

    const buildColoredSegments = (points) => {
        const segments = [];

        for (let i = 0; i < points.length - 1; i++) {
            const p1 = points[i];
            const p2 = points[i + 1];

            segments.push({
                path: [
                    { lat: p1.lat, lng: p1.lng },
                    { lat: p2.lat, lng: p2.lng }
                ],
                color: getSpeedColor(p1.velocidad || 0)
            });
        }

        return segments;
    };

    const [arrowSymbol, setArrowSymbol] = useState(null);

    useEffect(() => {
        if (!isLoaded || !window.google) return;

        // Se mantiene para no alterar lógica si se utiliza fuera, pero ya no pinta marcadores en el mapa
        setStopIcon({
            url: paradaIcon,
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 32)
        });
    }, [isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;

        const maps = window.google?.maps;
        if (!maps || !vehicleIcon) return;

        setMarkerIcon({
            url: vehicleIcon,
            scaledSize: new maps.Size(40, 40),
            anchor: new maps.Point(20, 20)
        });
    }, [isLoaded, vehicleIcon]);

    useEffect(() => {
        if (!isLoaded) return;

        const maps = window.google?.maps;
        const symbolPath = maps?.SymbolPath?.FORWARD_CLOSED_ARROW
            ?? maps?.SymbolPath?.FORWARD_OPEN_ARROW;

        if (!symbolPath) return;

        setArrowSymbol({
            path: symbolPath,
            scale: 1.6,
            strokeColor: "#1F6F7A",
            fillColor: "#1F6F7A",
            fillOpacity: 0.9,
            strokeWeight: 1
        });
    }, [isLoaded]);

    useEffect(() => {
        const maps = window.google?.maps;
        if (!isLoaded || !maps) return;

        const visible = trips.filter((_, i) => visibleTrips.has(i));
        const points = visible.flatMap(t => t.points);

        setPlayPoints(points);
        setPlayIndex(0);
        segmentRef.current = 0;
        tRef.current = 0;

        if (points.length) {
            const first = points[0];
            setMarkerPosition({
                lat: first.lat,
                lng: first.lng
            });

            if (mapRef.current) {
                const bounds = new window.google.maps.LatLngBounds();
                points.forEach(p => bounds.extend(p));
                mapRef.current.fitBounds(bounds);
            }
        }
    }, [visibleTrips, trips, isLoaded]);

    const grupos = trips.filter((_, i) => visibleTrips.has(i));
    
    const handlePolylineMouseOver = (e, grupo) => {
        const latLng = e.latLng;
        if (!latLng) return;

        const lat = latLng.lat();
        const lng = latLng.lng();

        const hayParadaCerca = isNearStop(lat, lng, grupos);

        if (hayParadaCerca) {
            setHoveredInfo(null);
            return;
        }

        const closest = grupo.points.reduce((prev, curr) => {
            const prevDist = Math.hypot(prev.lat - lat, prev.lng - lng);
            const currDist = Math.hypot(curr.lat - lat, curr.lng - lng);
            return currDist < prevDist ? curr : prev;
        });

        setHoveredInfo({
            position: { lat: closest.lat, lng: closest.lng },
            timestamp: closest.timestamp,
            speed: closest.velocidad,
            course: closest.rumbo
        });
    };

    // 🔥 LOGICA CORE: Calcular Clusters de Paradas dinámicamente
    const stopClusters = useMemo(() => {
        if (!isLoaded) return [];

        const allStops = [];
        grupos.forEach(grupo => {
            if (grupo.stops) {
                allStops.push(...grupo.stops);
            }
        });

        // A partir de zoom 16, se desarman todos los grupos y se muestran sueltas
        if (zoomActual >= 16) {
            return allStops.map(s => ({ isCluster: false, ...s }));
        }

        const threshold =
            zoomActual <= 10 ? 0.08 :
            zoomActual <= 12 ? 0.03 :
            zoomActual <= 14 ? 0.01 : 0.004;

        const result = [];
        const usados = new Set();

        for (let i = 0; i < allStops.length; i++) {
            if (usados.has(i)) continue;
            const s1 = allStops[i];
            const lat1 = Number(s1.lat);
            const lng1 = Number(s1.lng);
            if (isNaN(lat1) || isNaN(lng1)) continue;

            const clusterGroup = [s1];
            usados.add(i);

            for (let j = i + 1; j < allStops.length; j++) {
                if (usados.has(j)) continue;
                const s2 = allStops[j];
                const lat2 = Number(s2.lat);
                const lng2 = Number(s2.lng);
                if (isNaN(lat2) || isNaN(lng2)) continue;

                const distLat = Math.abs(lat1 - lat2);
                const distLng = Math.abs(lng1 - lng2);

                if (distLat < threshold && distLng < threshold) {
                    clusterGroup.push(s2);
                    usados.add(j);
                }
            }

            if (clusterGroup.length > 1) {
                const avgLat = clusterGroup.reduce((sum, s) => sum + Number(s.lat), 0) / clusterGroup.length;
                const avgLng = clusterGroup.reduce((sum, s) => sum + Number(s.lng), 0) / clusterGroup.length;
                const totalMinutes = clusterGroup.reduce((sum, s) => sum + (Number(s.minutes) || 0), 0);

                result.push({
                    isCluster: true,
                    lat: avgLat,
                    lng: avgLng,
                    totalStops: clusterGroup.length,
                    totalMinutes: totalMinutes,
                    stops: clusterGroup
                });
            } else {
                result.push({
                    isCluster: false,
                    ...s1
                });
            }
        }
        return result;
    }, [grupos, zoomActual, isLoaded]);

    return (
        <>
            <IconButton size="small" onClick={handleOpen}>
                <FaRoute color="#2b90fd" />
            </IconButton>
            <Modal open={open} onClose={handleClose}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "96%",
                        maxWidth: 1400,
                        height: "95%",
                        bgcolor: "#fff",
                        borderRadius: "16px",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden"
                    }}
                >
                    {/* HEADER */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            px: 3,
                            py: 2,
                            borderBottom: "1px solid #eee"
                        }}
                    >
                        <Typography variant="h6">
                            Historial de Ruta
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

                            <IconButton
                                onClick={() => {
                                    setTempStopMinutes(stopMaxMinutes);
                                    setOpenStopModal(true);
                                }}
                                sx={{
                                    width: 34,
                                    height: 34,
                                    background: "rgba(255, 87, 34, 0.12)",
                                    color: "#D84315"
                                }}
                            >
                                <StopIcon />
                            </IconButton>

                            {/* SWITCH PRO */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    background: showSpeedColors ? "#E0F2F1" : "#F1F5F9",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: "20px"
                                }}
                            >
                                <SpeedIcon sx={{ fontSize: 18 }} />

                                <Switch
                                    size="small"
                                    checked={showSpeedColors}
                                    onChange={(e) => setShowSpeedColors(e.target.checked)}
                                />

                                <Typography sx={{ fontSize: 12 }}>
                                    Velocidad
                                </Typography>

                            </Box>

                            {/* BOTÓN PRO */}
                            <Button
                                size="small"
                                onClick={() => setOpenLegend(true)}
                                sx={{
                                    borderRadius: "20px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    background: "linear-gradient(135deg, #2FA4B8, #1976D2)",
                                    color: "#fff",
                                    px: 2,
                                    "&:hover": {
                                        background: "linear-gradient(135deg, #258a9c, #1565C0)"
                                    }
                                }}
                            >
                                ⚙ Configurar
                            </Button>

                            {summary && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        px: 0.5,
                                        py: 0.5,
                                        borderBottom: "1px solid #eee",
                                        background: "#F8FAFC",
                                        flexWrap: "wrap"
                                    }}
                                >
                                    <Typography sx={{ fontSize: 13 }}>
                                        📊 Viajes: <b>{summary.total_trips}</b>
                                    </Typography>

                                    <Typography sx={{ fontSize: 13 }}>
                                        🚗 Km totales: <b>{summary.total_distance_km} km</b>
                                    </Typography>

                                    <Typography sx={{ fontSize: 13 }}>
                                        ⚡ Vel. promedio: <b>{summary.avg_speed_kmh} km/h</b>
                                    </Typography>

                                    <Typography sx={{ fontSize: 13 }}>
                                        🚀 Vel. máxima: <b>{summary.max_speed_kmh} km/h</b>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <IconButton onClick={handleClose}>✕</IconButton>

                    </Box>
                    {/* CONTROLES */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: 3,
                            py: 2,
                            borderBottom: "1px solid #eee",
                            flexWrap: "wrap"
                        }}
                    >
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Fecha"
                                value={selectedDate}
                                onChange={(d) => setSelectedDate(d)}
                                slotProps={{
                                    textField: { size: "small", sx: { maxWidth: 150 } }
                                }}
                            />
                        </LocalizationProvider>
                        <Button
                            variant="contained"
                            sx={botonCronos}
                            onClick={fetchRoute}
                            disabled={loading || !vehiculoId}
                        >
                            {loading
                                ? <CircularProgress size={20} />
                                : "Mostrar Ruta"}
                        </Button>

                        {/* PANEL PLAYBACK */}
                        <PlayBack
                            isPlaying={isPlaying}
                            startPlayback={startPlayback}
                            pausePlayback={pausePlayback}
                            playPoints={playPoints}
                            speed={speed}
                            changeSpeed={changeSpeed}
                            playIndex={playIndex}
                            setPlayIndex={setPlayIndex}
                            segmentRef={segmentRef}
                            tRef={tRef}
                            setMarkerPosition={setMarkerPosition}
                            currentPoint={currentPoint}
                            rumboTexto={rumboTexto}
                            speedRanges={speedRanges}
                        />

                    </Box>

                    {/* CONTENIDO */}
                    <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
                        {/* PANEL VIAJES */}
                        <Viajes
                            trips={trips}
                            visibleTrips={visibleTrips}
                            toggleTrip={toggleTrip}
                        />

                        {/* MAPA */}
                        <Box sx={{ flex: 1, position: "relative" }}>
                            {isLoaded && (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={defaultCenter}
                                    zoom={12}
                                    onLoad={onMapLoad}
                                    options={{
                                        streetViewControl: false,
                                        styles: mapStyles
                                    }}
                                    onZoomChanged={() => {
                                        if (!mapRef.current) return;
                                        const zoom = Math.round(mapRef.current.getZoom());
                                        setZoomActual((prev) => (prev === zoom ? prev : zoom));
                                    }}
                                >
                                    {grupos.map((grupo, index) => {
                                        const first = grupo.points[0];
                                        const last = grupo.points[grupo.points.length - 1];

                                        return (
                                            <React.Fragment key={`trip-markers-${index}`}>

                                                {/* 🔵 INICIO VIAJE */}
                                                {first && (
                                                    <Marker
                                                        position={{ lat: first.lat, lng: first.lng }}
                                                        icon={getTripMarkerIcon(index, "start")}
                                                        zIndex={900}
                                                        onMouseOver={() => {
                                                            setHoveredInfo({
                                                                position: { lat: first.lat, lng: first.lng },
                                                                type: "start",
                                                                trip: index + 1,
                                                                timestamp: first.timestamp
                                                            });
                                                        }}
                                                        onMouseOut={() => setHoveredInfo(null)}
                                                    />
                                                )}

                                                {/* 🔴 FIN VIAJE */}
                                                {last && (
                                                    <Marker
                                                        position={{ lat: last.lat, lng: last.lng }}
                                                        icon={getTripMarkerIcon(index, "end")}
                                                        onMouseOver={() => {
                                                            setHoveredInfo({
                                                                position: { lat: last.lat, lng: last.lng },
                                                                type: "end",
                                                                trip: index + 1,
                                                                timestamp: last.timestamp
                                                            });
                                                        }}
                                                        onMouseOut={() => setHoveredInfo(null)}
                                                    />
                                                )}

                                            </React.Fragment>
                                        );
                                    })}

                                    {/* 🔥 RENDERIZADO UNIFICADO DE PARADAS (Clusters y Solos) */}
                                    {stopClusters.map((cluster, idx) => {
                                        const lat = Number(cluster.lat);
                                        const lng = Number(cluster.lng);
                                        const isCluster = cluster.isCluster;
                                        const minutes = isCluster ? cluster.totalMinutes : (cluster.minutes || 0);
                                        const stopsCount = isCluster ? cluster.totalStops : 1;
                                        const timestamp = cluster.start_time;

                                        return (
                                            <Marker
                                                key={`stop-marker-${idx}`}
                                                position={{ lat, lng }}
                                                icon={createStopClusterIcon(minutes)}
                                                zIndex={998}
                                                onClick={() => {
                                                    if (mapRef.current) {
                                                        mapRef.current.panTo({ lat, lng });
                                                        mapRef.current.setZoom(Math.min(zoomActual + 2, 18));
                                                    }
                                                }}
                                                onMouseOver={() => {
                                                    setHoveredInfo({
                                                        position: { lat, lng },
                                                        stop: true,
                                                        isCluster: isCluster,
                                                        totalStops: stopsCount,
                                                        minutes: minutes,
                                                        timestamp: timestamp
                                                    });
                                                }}
                                                onMouseOut={() => setHoveredInfo(null)}
                                            />
                                        );
                                    })}

                                    {grupos.map((grupo, index) => {
                                        // 👉 MODO NORMAL (línea tipo Google Maps)
                                        if (!showSpeedColors) {
                                            return (
                                                <Polyline
                                                    key={index}
                                                    path={grupo.points.map(p => ({
                                                        lat: p.lat,
                                                        lng: p.lng
                                                    }))}
                                                    options={{
                                                        strokeColor: "#2FA4B8",
                                                        strokeOpacity: 0.9,
                                                        strokeWeight: 5,
                                                        icons: arrowSymbol
                                                            ? [
                                                                {
                                                                    icon: arrowSymbol,
                                                                    offset: "0",
                                                                    repeat: "80px"
                                                                }
                                                            ]
                                                            : []
                                                    }}
                                                    onMouseOver={e => handlePolylineMouseOver(e, grupo)}
                                                    onMouseOut={() => setHoveredInfo(null)}
                                                />
                                            );
                                        }

                                        // 👉 MODO VELOCIDAD (multicolor)
                                        const segments = buildColoredSegments(grupo.points);

                                        return segments.map((seg, i) => (
                                            <Polyline
                                                key={`${index}-${i}`}
                                                path={seg.path}
                                                options={{
                                                    strokeColor: seg.color,
                                                    strokeOpacity: 0.95,
                                                    strokeWeight: 5
                                                }}
                                                onMouseOver={e => handlePolylineMouseOver(e, grupo)}
                                                onMouseOut={() => setHoveredInfo(null)}
                                            />
                                        ));
                                    })}
                                    
                                    {markerPosition && playPoints.length > 0 && playPoints[playIndex] && (
                                        <Marker
                                            position={markerPosition}
                                            icon={markerIcon || undefined}
                                            zIndex={1000}
                                        />
                                    )}

                                    {/* 🔥 TOOLTIP MODERNO MEJORADO VISUALMENTE */}
                                    {hoveredInfo && (
                                        <OverlayView
                                            position={hoveredInfo.position}
                                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                        >
                                            <Box
                                                sx={{
                                                    background: "#fff",
                                                    borderRadius: "10px",
                                                    px: 2,
                                                    py: 1.5,
                                                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                                                    minWidth: 170,
                                                    borderLeft: hoveredInfo.stop ? "4px solid #ef4444" : "4px solid #2FA4B8"
                                                }}
                                            >
                                                {hoveredInfo.stop ? (
                                                    <>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#b91c1c" }}>
                                                            {hoveredInfo.isCluster ? `🛑 Agrupación (${hoveredInfo.totalStops})` : `🛑 Parada Fija`}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 13, mt: 0.5, color: "#334155" }}>
                                                            <b>⏱ {hoveredInfo.minutes} min</b> detenido{hoveredInfo.isCluster && "s en la zona"}
                                                        </Typography>
                                                        {!hoveredInfo.isCluster && hoveredInfo.timestamp && (
                                                            <Typography sx={{ fontSize: 11, mt: 1, color: "#64748b", fontWeight: 500 }}>
                                                                🕒 Inicio: {new Date(hoveredInfo.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                                            {new Date(hoveredInfo.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 13, mt: 0.5, color: "#334155" }}>
                                                            🚗 Velocidad: <b>{hoveredInfo.speed} km/h</b>
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </OverlayView>
                                    )}
                                </GoogleMap>
                            )}
                            
                            {/* 🔥 LEYENDA ENCIMA DEL MAPA */}
                            {showSpeedColors && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        bottom: 20,
                                        left: 20,
                                        background: "#fff",
                                        borderRadius: 2,
                                        boxShadow: 3,
                                        px: 2,
                                        py: 1,
                                        zIndex: 10
                                    }}
                                >
                                    {speedRanges.map((r, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    backgroundColor: r.color,
                                                    borderRadius: "2px"
                                                }}
                                            />
                                            <Typography sx={{ fontSize: 11 }}>
                                                {getRangeLabel(r)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Modal open={openLegend} onClose={() => setOpenLegend(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 380,
                        bgcolor: "#fff",
                        borderRadius: "20px",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                        p: 3
                    }}
                >
                    <Typography variant="h6" mb={2} fontWeight={600}>
                        🎨 Configurar Velocidades
                    </Typography>

                    {speedRanges.map((range, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mb: 2
                            }}
                        >
                            {/* COLOR PICKER */}
                            <input
                                type="color"
                                value={range.color}
                                onChange={(e) => {
                                    const newRanges = [...speedRanges];
                                    newRanges[index].color = e.target.value;
                                    setSpeedRanges(newRanges);
                                }}
                                style={{
                                    width: 35,
                                    height: 35,
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer"
                                }}
                            />

                            {/* MIN */}
                            <TextField
                                size="small"
                                type="number"
                                value={range.min}
                                disabled={index === 0}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    const newRanges = [...speedRanges];

                                    newRanges[index].min = value;

                                    if (index > 0) {
                                        newRanges[index - 1].max = value;
                                    }

                                    setSpeedRanges(newRanges);
                                }}
                                sx={{ width: 70 }}
                            />

                            <Typography>-</Typography>

                            {/* MAX */}
                            {range.max !== Infinity ? (
                                <TextField
                                    size="small"
                                    type="number"
                                    value={range.max}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        const newRanges = [...speedRanges];

                                        newRanges[index].max = value;

                                        if (newRanges[index + 1]) {
                                            newRanges[index + 1].min = value;
                                        }

                                        setSpeedRanges(newRanges);
                                    }}
                                    sx={{ width: 70 }}
                                />
                            ) : (
                                <Typography sx={{ fontSize: 13 }}>∞</Typography>
                            )}

                            {/* LABEL DINÁMICO */}
                            <Typography sx={{ fontSize: 12, flex: 1 }}>
                                {getRangeLabel(range)}
                            </Typography>
                        </Box>
                    ))}

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => setOpenLegend(false)}
                        sx={{
                            mt: 2,
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 600
                        }}
                    >
                        Aplicar
                    </Button>
                </Box>
            </Modal>

            <Modal open={openStopModal} onClose={() => setOpenStopModal(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "#fff",
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 3,
                        width: 320
                    }}
                >
                    <Typography fontWeight={600} mb={1}>
                        🛑 Configurar paradas
                    </Typography>

                    <Typography sx={{ fontSize: 12, color: "#64748B", mb: 2 }}>
                        Si el tiempo entre dos puntos es mayor a los minutos definidos,
                        se considerará como un nuevo viaje.
                    </Typography>

                    <TextField
                        fullWidth
                        type="number"
                        label="Minutos máximos de parada"
                        value={tempStopMinutes}
                        onChange={(e) => setTempStopMinutes(Number(e.target.value))}
                    />

                    <Stack direction="row" spacing={1} mt={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setOpenStopModal(false)}
                        >
                            Cancelar
                        </Button>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => {
                                const value = tempStopMinutes;

                                setStopMaxMinutes(value);
                                setOpenStopModal(false);

                                fetchRouteWithStops(value);
                            }}
                        >
                            Aplicar
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
};

export default ModalHistorial;